/**
 * Product gallery zoom lightbox.
 *
 * Click any thumbnail tile → fullscreen overlay with a 2400px image.
 * Desktop: click the image to toggle 1×/2.5× zoom centered on the click point;
 *          drag to pan, arrow keys for prev/next, Esc to close.
 * Mobile:  pinch-to-zoom (1×–4×), double-tap to toggle 1×/2×,
 *          swipe horizontally for prev/next, swipe down to close.
 *
 * Cmd/Ctrl/middle-click on a tile keeps the native "open image in new tab"
 * behavior — the underlying <a href="..."> is preserved as a no-JS fallback.
 */

const DESKTOP_ZOOM = 2.5;
const DOUBLE_TAP_MS = 300;
const SWIPE_THRESHOLD = 50;
const SWIPE_DOWN_CLOSE = 90;
const TAP_SLOP = 10;

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.zoom = this.querySelector('[data-zoom]');
    this.tiles = [...this.querySelectorAll('[data-image-tile]')];
    if (!this.zoom || !this.tiles.length) return;

    this.frames = [...this.zoom.querySelectorAll('[data-zoom-frame]')];
    this.images = [...this.zoom.querySelectorAll('[data-zoom-image]')];
    this.thumbs = [...this.zoom.querySelectorAll('[data-zoom-thumb]')];
    this.stage = this.zoom.querySelector('[data-zoom-stage]');
    this.counterEl = this.zoom.querySelector('[data-zoom-current]');
    this.hintEl = this.zoom.querySelector('[data-zoom-hint]');

    this.index = 0;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.isOpen = false;

    this.#wireTileClicks();
    this.#wireControls();
    this.#wireKeyboard();
    this.#wireDesktopZoom();
    this.#wireTouch();
  }

  // ── Tile clicks: open lightbox at that index, or fall through on cmd/ctrl/middle ──
  #wireTileClicks() {
    this.tiles.forEach((tile, i) => {
      let touchStartX = 0;
      let touchStartY = 0;

      tile.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        },
        { passive: true }
      );

      tile.addEventListener('click', (e) => {
        // Let modifier-clicks (cmd/ctrl/middle) navigate to the raw image.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

        // Suppress clicks that came from a horizontal swipe in the carousel.
        if (e.changedTouches?.[0]) {
          const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
          const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
          if (dx > TAP_SLOP || dy > TAP_SLOP) {
            e.preventDefault();
            return;
          }
        }

        e.preventDefault();
        this.open(i);
      });
    });
  }

  #wireControls() {
    this.zoom.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.close());
    this.zoom.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.zoom.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.go(+thumb.dataset.index));
    });
  }

  #wireKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(this.index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(this.index + 1);
      }
    });
  }

  // ── Open / close ──
  open(index) {
    this.zoom.removeAttribute('hidden');
    // Force reflow so the opacity transition runs.
    void this.zoom.offsetWidth;
    this.zoom.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.go(index, true);
    requestAnimationFrame(() => this.zoom.querySelector('[data-zoom-close]')?.focus());
  }

  close() {
    this.zoom.dataset.open = 'false';
    this.isOpen = false;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    this.#resetZoom();
    setTimeout(() => {
      if (!this.isOpen) this.zoom.setAttribute('hidden', '');
    }, 200);
    this.tiles[this.index]?.focus({ preventScroll: true });
  }

  // ── Navigate ──
  go(index, immediate = false) {
    const n = this.frames.length;
    index = ((index % n) + n) % n;
    this.index = index;
    this.#resetZoom();

    this.frames.forEach((frame, i) => {
      frame.dataset.active = (i === index).toString();
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.dataset.active = (i === index).toString();
    });
    if (this.counterEl) this.counterEl.textContent = String(index + 1);

    // Scroll the active thumb into view (centered horizontally).
    const activeThumb = this.thumbs[index];
    if (activeThumb && this.isOpen) {
      activeThumb.scrollIntoView({
        behavior: immediate ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    // Lazy-load active image + neighbors.
    [-1, 0, 1].forEach((off) => {
      const ni = ((index + off) % n + n) % n;
      const img = this.images[ni];
      if (img && !img.src && img.dataset.src) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      }
    });
  }

  // ── Zoom transform ──
  #applyTransform() {
    const img = this.images[this.index];
    if (!img) return;
    img.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
  }

  #resetZoom() {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.images.forEach((img) => {
      img.style.transform = '';
      img.style.transition = '';
      img.style.cursor = '';
    });
  }

  /** Clamp pan so the scaled image never reveals empty space past its bounds. */
  #constrain() {
    const img = this.images[this.index];
    if (!img) return;
    // Use the image's *unscaled* layout box; scale is applied via transform.
    const rect = img.getBoundingClientRect();
    const stage = this.stage.getBoundingClientRect();
    const overflowX = Math.max(0, (rect.width - stage.width) / 2);
    const overflowY = Math.max(0, (rect.height - stage.height) / 2);
    this.tx = Math.max(-overflowX, Math.min(overflowX, this.tx));
    this.ty = Math.max(-overflowY, Math.min(overflowY, this.ty));
  }

  // ── Desktop: click to toggle zoom, drag to pan ──
  #wireDesktopZoom() {
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartTx = 0;
    let dragStartTy = 0;
    let didDrag = false;

    const isDesktop = () => window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches;

    this.stage.addEventListener('pointerdown', (e) => {
      if (!isDesktop() || e.pointerType !== 'mouse') return;
      const img = e.target.closest('[data-zoom-image]');
      if (!img) return;
      // Only drag when zoomed in.
      if (this.scale === 1) return;
      isDragging = true;
      didDrag = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragStartTx = this.tx;
      dragStartTy = this.ty;
      img.style.transition = 'none';
      img.style.cursor = 'grabbing';
      img.setPointerCapture(e.pointerId);
    });

    this.stage.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag = true;
      this.tx = dragStartTx + dx;
      this.ty = dragStartTy + dy;
      this.#constrain();
      this.#applyTransform();
    });

    const endDrag = (e) => {
      if (!isDragging) return;
      isDragging = false;
      const img = this.images[this.index];
      if (img) {
        img.style.transition = '';
        img.style.cursor = 'grab';
      }
      try { e.target.releasePointerCapture?.(e.pointerId); } catch {}
    };
    this.stage.addEventListener('pointerup', endDrag);
    this.stage.addEventListener('pointercancel', endDrag);

    // Click backdrop (not the image) → close.
    this.stage.addEventListener('click', (e) => {
      const img = e.target.closest('[data-zoom-image]');

      if (!img) {
        // Click was on stage padding, not on an image → close (desktop only;
        // on mobile the stage is the swipe area, so don't close on tap).
        if (isDesktop()) this.close();
        return;
      }

      if (!isDesktop()) return;
      if (didDrag) {
        didDrag = false;
        return;
      }

      // Toggle zoom.
      if (this.scale === 1) {
        const rect = img.getBoundingClientRect();
        const cx = e.clientX - rect.left - rect.width / 2;
        const cy = e.clientY - rect.top - rect.height / 2;
        this.scale = DESKTOP_ZOOM;
        this.tx = -cx * (this.scale - 1);
        this.ty = -cy * (this.scale - 1);
        this.#constrain();
        img.style.transition = 'transform 0.2s ease-out';
        this.#applyTransform();
        img.style.cursor = 'grab';
      } else {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        img.style.transition = 'transform 0.2s ease-out';
        this.#applyTransform();
        img.style.cursor = '';
      }
    });
  }

  // ── Mobile: pinch zoom, double-tap, swipe ──
  #wireTouch() {
    let mode = 'idle'; // 'idle' | 'pan' | 'pinch' | 'swipe'
    let startX = 0;
    let startY = 0;
    let startTx = 0;
    let startTy = 0;
    let pinchStartDist = 0;
    let pinchStartScale = 1;
    let pinchCenterX = 0;
    let pinchCenterY = 0;
    let lastTapAt = 0;
    let lastTapX = 0;
    let lastTapY = 0;
    let hintHidden = false;

    const hideHint = () => {
      if (hintHidden || !this.hintEl) return;
      hintHidden = true;
      this.hintEl.style.opacity = '0';
    };

    const dist = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

    this.stage.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 2) {
          mode = 'pinch';
          pinchStartDist = dist(e.touches[0], e.touches[1]);
          pinchStartScale = this.scale;
          pinchCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          pinchCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          hideHint();
        } else if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startTx = this.tx;
          startTy = this.ty;
          mode = this.scale > 1 ? 'pan' : 'swipe';

          // Double-tap detection (only when zoomed out → zoom in; when zoomed in → reset).
          const now = Date.now();
          const tapDx = Math.abs(startX - lastTapX);
          const tapDy = Math.abs(startY - lastTapY);
          if (now - lastTapAt < DOUBLE_TAP_MS && tapDx < 30 && tapDy < 30) {
            e.preventDefault();
            const img = this.images[this.index];
            if (img) {
              if (this.scale === 1) {
                const rect = img.getBoundingClientRect();
                const cx = startX - rect.left - rect.width / 2;
                const cy = startY - rect.top - rect.height / 2;
                this.scale = 2;
                this.tx = -cx * (this.scale - 1);
                this.ty = -cy * (this.scale - 1);
              } else {
                this.scale = 1;
                this.tx = 0;
                this.ty = 0;
              }
              this.#constrain();
              img.style.transition = 'transform 0.2s ease-out';
              this.#applyTransform();
            }
            lastTapAt = 0;
            mode = 'idle';
            hideHint();
            return;
          }
          lastTapAt = now;
          lastTapX = startX;
          lastTapY = startY;
        }
      },
      { passive: false }
    );

    this.stage.addEventListener(
      'touchmove',
      (e) => {
        if (mode === 'pinch' && e.touches.length === 2) {
          e.preventDefault();
          const d = dist(e.touches[0], e.touches[1]);
          this.scale = Math.max(1, Math.min(4, pinchStartScale * (d / pinchStartDist)));
          // Keep the pinch midpoint anchored as the user spreads/pinches.
          const img = this.images[this.index];
          if (img) {
            const rect = img.getBoundingClientRect();
            const cx = pinchCenterX - rect.left - rect.width / 2;
            const cy = pinchCenterY - rect.top - rect.height / 2;
            this.tx = -cx * (this.scale - 1);
            this.ty = -cy * (this.scale - 1);
          }
          this.#constrain();
          const cur = this.images[this.index];
          if (cur) cur.style.transition = 'none';
          this.#applyTransform();
        } else if (mode === 'pan' && e.touches.length === 1) {
          e.preventDefault();
          this.tx = startTx + (e.touches[0].clientX - startX);
          this.ty = startTy + (e.touches[0].clientY - startY);
          this.#constrain();
          const cur = this.images[this.index];
          if (cur) cur.style.transition = 'none';
          this.#applyTransform();
        }
        // mode === 'swipe' lets touchend decide prev/next/close.
      },
      { passive: false }
    );

    this.stage.addEventListener('touchend', (e) => {
      if (mode === 'pinch') {
        // If pinched back to (near) 1×, snap fully out.
        if (this.scale < 1.05) {
          this.#resetZoom();
        }
        mode = 'idle';
        return;
      }
      if (mode === 'swipe' && e.changedTouches.length === 1) {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD) {
          this.go(this.index + (dx < 0 ? 1 : -1));
        } else if (dy > SWIPE_DOWN_CLOSE && Math.abs(dy) > Math.abs(dx)) {
          this.close();
        }
      }
      mode = 'idle';
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

export { ProductGallery };
